import React, { useEffect } from "react";
import { Helmet } from "react-helmet";
import 'bootstrap/dist/css/bootstrap.min.css';
import AOS from 'aos';
import 'aos/dist/aos.css'; // Import AOS styles
import './App.css';
import { FaRegFilePdf, FaShareAlt, FaPrint, FaDownload, FaMobileAlt, FaEye, FaGooglePlay } from "react-icons/fa";

// Import your images here
import pdfFilesImage from './your-pdf-files-image.png';
import comfortReadImage from './your-comfort-read-image.png';
import sharePrintImage from './your-share-print-image.png';
import manageFilesImage from './your-manage-files-image.png'; // Adjust the path if necessary
const currentYear = new Date().getFullYear();

function App() {
  useEffect(() => {
    AOS.init({
      duration: 1000, // Animation duration
      once: true,     // Animation occurs only once
    });
  }, []);

  return (
    <div>
      {/* SEO Metadata */}
      <Helmet>
        <title>PDF Reader App - Free PDF Viewer & Reader for Android</title>
        <meta
          name="description"
          content="Download the best PDF Reader app for Android. View, read, print, and share all your PDF files easily. Get it from Play Store now!"
        />
        <meta name="keywords" content="PDF, PDF Reader, PDF Viewer, PDF Reader App, PDF Viewer for Android, Free PDF Reader, PDF Reader with Print, pdf app free download for android, best pdf book reader app for android, best pdf reader app for android free download, pdf reader free android" />
      </Helmet>

      {/* Header Section */}
      <header className="bg-primary text-white text-center py-5">
        <div className="container">
          <h1 className="display-4" data-aos="fade-down">Download PDF Reader App</h1>
          <p className="lead text-white" data-aos="fade-up">Free PDF Viewer & Reader - Available on Play Store</p>
          <a href="https://play.google.com/store/apps/details?id=com.darknightcode.allpdfreader" className="btn btn-light btn-lg mt-3" data-aos="fade-up">
            <FaGooglePlay /> Download from Play Store
          </a>
        </div>
      </header>
 
    <section className="container my-5">
      {/* 1st Feature: Get All PDF Files in One Place */}
      <div className="row align-items-center mb-5">
        <div className="col-md-6" data-aos="fade-up">
          <div className="custom-img-wrapper">
            <img src={pdfFilesImage} alt="Get all your PDF files in one place" className="custom-img" />
          </div>
        </div>
        <div className="col-md-6" data-aos="fade-up">
          <h3>Get all your PDF files in one place</h3>
          <p>
            With our PDF reader, you can easily get all your PDF files in one organized place. Whether you have invoices, 
            ebooks, manuals, or reports, they are automatically fetched and listed. Say goodbye to searching across multiple 
            folders! Our app makes document management easier than ever, giving you all your PDFs at your fingertips in one 
            central location. Perfect for those looking for an easy-to-use, all-in-one <strong>PDF manager </strong>. 
            This functionality is ideal for both work and study.
          </p>
          <a href="https://play.google.com/store/apps/details?id=com.darknightcode.allpdfreader" className="btn btn-primary" data-aos="fade-up" data-aos-delay="200">
            <FaGooglePlay /> Download Now
          </a>
        </div>
      </div>

      {/* 2nd Feature: Read PDF Files with Comfort */}
      <div className="row align-items-center mb-5">
        <div className="col-md-6 order-md-2" data-aos="fade-up">
          <div className="custom-img-wrapper">
            <img src={comfortReadImage} alt="Read PDF files with comfort" className="custom-img" />
          </div>
        </div>
        <div className="col-md-6 order-md-1" data-aos="fade-up">
          <h3>Read PDF files with comfort</h3>
          <p>
            Enjoy reading your PDF documents like never before with our optimized reading modes. Whether it's bright daytime 
            or late-night reading, our app provides a comfortable reading experience with adjustable <strong>night mode </strong> 
            and <strong>day mode </strong> features. No more straining your eyes! This PDF viewer is specifically designed to 
            enhance readability, making it perfect for long reading sessions such as ebooks, academic papers, or important 
            documents. You'll love how easy and comfortable it is to read PDFs with our app.
          </p>
          <a href="https://play.google.com/store/apps/details?id=com.darknightcode.allpdfreader" className="btn btn-primary" data-aos="fade-up" data-aos-delay="200">
            <FaGooglePlay /> Download Now
          </a>
        </div>
      </div>

      {/* 3rd Feature: Share or Print Your Documents */}
      <div className="row align-items-center mb-5">
        <div className="col-md-6" data-aos="fade-up">
          <div className="custom-img-wrapper">
            <img src={sharePrintImage} alt="Share or print your documents" className="custom-img" />
          </div>
        </div>
        <div className="col-md-6" data-aos="fade-up">
          <h3>Share or print your documents</h3>
          <p>
            Sharing and printing your PDF files has never been easier. With just a few taps, you can <strong>share PDF documents </strong> 
            via email, messaging apps, or cloud services directly from the app. Need a hard copy? You can also print PDFs 
            right from your device, hassle-free. This feature is perfect for professionals, students, and anyone who needs 
            quick and efficient document handling. Whether you’re sharing contracts, school assignments, or presentations, 
            our app simplifies the entire process.
          </p>
          <a href="https://play.google.com/store/apps/details?id=com.darknightcode.allpdfreader" className="btn btn-primary" data-aos="fade-up" data-aos-delay="200">
            <FaGooglePlay /> Download Now
          </a>
        </div>
      </div>

      {/* 4th Feature: Manage All Your PDF Files */}
      <div className="row align-items-center mb-5">
        <div className="col-md-6 order-md-2" data-aos="fade-up">
          <div className="custom-img-wrapper">
            <img src={manageFilesImage} alt="Manage all your PDF files" className="custom-img" />
          </div>
        </div>
        <div className="col-md-6 order-md-1" data-aos="fade-up">
          <h3>Manage all your PDF files</h3>
          <p>
            Our app is designed to be a powerful <strong>PDF file manager </strong> that helps you organize, rename, delete, 
            and sort your PDF files with ease. Whether you have a large library of documents or just a few, this feature 
            ensures that your files are always accessible and well-organized. You can rename files for clarity, delete 
            old documents, and sort them by date or size for quicker access. It’s the ultimate tool for staying organized 
            and efficient in handling all your PDF files.
          </p>
          <a href="https://play.google.com/store/apps/details?id=com.darknightcode.allpdfreader" className="btn btn-primary" data-aos="fade-up" data-aos-delay="200">
            <FaGooglePlay /> Download Now
          </a>
        </div>
      </div>
    </section>
  
    {/* Features Section */}
    <section className="container my-5">
      <h2 className="text-center mb-4" data-aos="fade-up">Why Choose Our PDF Reader?</h2>
      <div className="row">
        <div className="col-md-4 text-center" data-aos="fade-up">
          <FaRegFilePdf size={48} className="text-primary mb-3" />
          <h3>View & Read PDFs</h3>
          <p>Open and view PDF files quickly. Switch to reading mode for better comfort, making it easier to read long documents.</p>
        </div>
        <div className="col-md-4 text-center" data-aos="fade-up" data-aos-delay="100">
          <FaEye size={48} className="text-primary mb-3" />
          <h3>Reading Mode</h3>
          <p>Enable night mode for a comfortable reading experience in low light conditions. Read your PDFs with ease!</p>
        </div>
        <div className="col-md-4 text-center" data-aos="fade-up" data-aos-delay="200">
          <FaMobileAlt size={48} className="text-primary mb-3" />
          <h3>All PDFs in One Place</h3>
          <p>Automatically fetch and list all your PDF files in one place. No more searching for documents across folders.</p>
        </div>
      </div>
      <div className="row mt-4">
        <div className="col-md-4 text-center" data-aos="fade-up" data-aos-delay="300">
          <FaShareAlt size={48} className="text-primary mb-3" />
          <h3>Share & Print</h3>
          <p>Easily share or print your PDF files directly from the app. No complicated steps involved, just a few taps!</p>
        </div>
        <div className="col-md-4 text-center" data-aos="fade-up" data-aos-delay="400">
          <FaPrint size={48} className="text-primary mb-3" />
          <h3>Print PDF Files</h3>
          <p>Print your important PDF documents on the go. Simple and efficient printing directly from the app.</p>
        </div>
        <div className="col-md-4 text-center" data-aos="fade-up" data-aos-delay="500">
          <FaDownload  size={48} className="text-primary mb-3" />
          <h3>Lightweight & Fast</h3>
          <p>Our PDF reader is optimized for speed and lightweight design, providing a seamless experience even with large files.</p>
        </div>
      </div>
    </section>

    {/* FAQ Section */}
    <section className="container my-5">
      <h2 className="text-center mb-4" data-aos="fade-up">Frequently Asked Questions</h2>
      <div className="row">
        <div className="col-md-6" data-aos="fade-up">
          <h4>1. Is the PDF Reader app free to use?</h4>
          <p>Yes, the PDF Reader app is completely free to download and use. You can view, read, manage, and share PDF files without any hidden charges.</p>
        </div>
        <div className="col-md-6" data-aos="fade-up">
          <h4>2. Can I print PDF documents directly from the app?</h4>
          <p>Yes, the app allows you to print PDF documents directly from your Android device. Simply tap the print option, and your document will be ready for hard copy.</p>
        </div>
      </div>
      <div className="row mt-4">
        <div className="col-md-6" data-aos="fade-up">
          <h4>3. How does the night mode feature work?</h4>
          <p>Night mode adjusts the brightness and contrast to provide a comfortable reading experience in low-light conditions, reducing eye strain during nighttime reading.</p>
        </div>
        <div className="col-md-6" data-aos="fade-up">
          <h4>4. Can I manage my PDF files with this app?</h4>
          <p>Yes, you can easily manage all your PDF files with this app. You can organize, rename, delete, and sort files to keep everything well-structured and easy to access.</p>
          </div>
      </div>
    </section>

    {/* Call to Action Section */}
    <section className="text-center my-5">
      <h2 data-aos="fade-up">Download the PDF Reader App Now</h2>
      <p data-aos="fade-up" data-aos-delay="100">Manage all your PDF documents with ease. Free, simple, and powerful!</p>
      <a href="https://play.google.com/store/apps/details?id=com.darknightcode.allpdfreader" className="btn btn-primary btn-lg" data-aos="fade-up" data-aos-delay="200">
        <FaGooglePlay /> Get it on Play Store
      </a>
    </section>

    {/* Footer */}
    <footer className="bg-dark text-white text-center py-4">
      <div className="container" data-aos="fade-up">
        <p>
          © {currentYear} Darknight Code | 
          <a href="https://darknightcodecompany.blogspot.com/2022/12/privacy-policy.html" className="ml-2 text-decoration-none"> Privacy Policy</a>
        </p>
      </div>
    </footer>
    </div>
  );
}


export default App;
